import { Component, EventEmitter, input, output, signal, computed } from '@angular/core';
import { ImageService } from '../../../../services/image.service';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { SearchFormComponent } from '../../../forms/standard/search-form/search-form.component';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
	selector: 'app-previous-image-selector',
	standalone: true,
	imports: [AngularModule, ZorroModule, SearchFormComponent],
	templateUrl: './previous-image-selector.component.html',
	styleUrl: './previous-image-selector.component.scss',
})
export class PreviousImageSelectorComponent {
	public table = input<string | undefined>();
	public column = input<string | undefined>();
	public onSelectImageUrl = output<string>();
	public onClose = output<void>();
	public loading = signal(true);
	public searchInProgress = signal(false);
	public preloadProgress = signal(0);
	public totalImages = signal(0);
	public urls = signal<string[]>([]);
	public filteredUrls = signal<string[]>([]);
	public searchTerm = signal('');
	public whichUrls = computed(() => (this.searchInProgress() ? this.filteredUrls() : this.urls()));

	constructor(
		public imageService: ImageService,
		public sanitizer: DomSanitizer
	) {}

	ngOnInit() {
		const tableVal = this.table();
		const columnVal = this.column();

		if (!tableVal || !columnVal) {
			console.error('Table and column parameters are required');
			return;
		}

		this.imageService.getExistingImageUrlsByPath$(tableVal, columnVal).then((urls: string[]) => {
			this.totalImages.set(urls.length);

			if (urls.length === 0) {
				this.loading.set(false);
				return;
			}

			this.preloadImagesInBatches(urls);
		});
	}

	private async preloadImagesInBatches(allUrls: string[]): Promise<void> {
		const batchSize = 40;
		const totalImages = allUrls.length;
		let processedCount = 0;

		for (let i = 0; i < allUrls.length; i += batchSize) {
			const batch = allUrls.slice(i, i + batchSize);
			const validUrlsFromBatch = await Promise.all(batch.map(url => this.preloadSingleImage(url)));

			const validUrls = validUrlsFromBatch.filter(url => url !== null) as string[];
			this.urls.update(current => [...current, ...validUrls]);

			processedCount += batch.length;
			this.preloadProgress.set(Math.round((processedCount / totalImages) * 100));

			this.loading.set(false);
		}
	}

	private preloadSingleImage(url: string): Promise<string | null> {
		return new Promise(resolve => {
			const img = new Image();

			img.src = url;

			img.onload = () => resolve(url);

			img.onerror = () => {
				console.error('Error loading image:', url);
				resolve(null);
			};
		});
	}

	public search($event: any): void {
		const search: string = $event.search;
		this.searchTerm.set(search);
		this.searchInProgress.set(search !== '');

		this.filteredUrls.set(this.urls().filter((url: string) => url.toLowerCase().includes(search.toLowerCase())));
	}

	public selectImageUrl(url: string) {
		this.onSelectImageUrl.emit(url);
	}
}
